import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Download, Mail, Copy, MessageSquare, Clock, User, Calendar,
  X, Send, Loader2, TrendingUp, Eye, MousePointerClick,
} from "lucide-react";

const PIPELINE = [
  { value: "new", label: "New Inquiry" },
  { value: "contacted", label: "Contacted" },
  { value: "consultation_scheduled", label: "Consultation Scheduled" },
  { value: "invoice_sent", label: "Invoice Sent" },
  { value: "payment_received", label: "Payment Received" },
  { value: "in_production", label: "In Production" },
  { value: "completed", label: "Completed" },
  { value: "closed", label: "Closed" },
];

const STAGE_COLORS = {
  new: "#c8a24a",
  contacted: "#3d7f9c",
  consultation_scheduled: "#5a5a5a",
  invoice_sent: "#946600",
  payment_received: "#4a7c3e",
  in_production: "#2f5f3f",
  completed: "#1a4a1a",
  closed: "#666",
};

const AdminTurnkeyInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (search) params.set("q", search);
    api.get(`/admin/turnkey-inquiries?${params.toString()}`)
      .then((r) => setInquiries(r.data.inquiries || []))
      .catch(() => setInquiries([]))
      .finally(() => setLoading(false));
    api.get("/admin/turnkey-marketplace-analytics")
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  };

  // Debounced reload on filter/search change. `load` is intentionally NOT in
  // the dep array — it's re-created every render, so including it would trigger
  // an infinite loop (effect → setState → new load ref → effect → …).
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [statusFilter, search]);

  const stageCounts = useMemo(() => {
    const c = {};
    inquiries.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [inquiries]);

  const exportCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/turnkey-inquiries/export.csv`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const dl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = dl;
        a.download = `turnkey-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(dl);
      })
      .catch(() => toast.error("CSV export failed"));
  };

  const updateInquiry = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/turnkey-inquiries/${id}`, patch);
      setInquiries((list) => list.map((x) => x.id === id ? data.inquiry : x));
      if (selected?.id === id) setSelected(data.inquiry);
      toast.success("Updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success(`Copied ${email}`);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Turnkey Marketplace CRM</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-inquiries-title">
            Command Center · Inquiries
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Every prospect inquiry, in one pipeline. Track outreach, add internal notes, set
            follow-up dates, and export to CSV.
          </p>
        </div>
        <button onClick={exportCSV} className="ft-btn-primary" data-testid="admin-inquiries-export">
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* ANALYTICS STRIP */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="admin-inquiries-analytics">
          {[
            { label: "Total Views", value: analytics.totals.total_views, Icon: Eye },
            { label: "Total Inquiries", value: analytics.totals.total_inquiries, Icon: MessageSquare },
            { label: "Conversion", value: `${analytics.totals.overall_conversion_rate}%`, Icon: TrendingUp },
            { label: "Most Viewed", value: analytics.totals.most_viewed || "—", Icon: Eye, small: true },
            { label: "Most Inquired", value: analytics.totals.most_inquired || "—", Icon: MousePointerClick, small: true },
          ].map((k) => (
            <div key={k.label} className="ft-card !p-4">
              <div className="flex items-center justify-between">
                <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">
                  {k.label}
                </div>
                <k.Icon size={14} className="text-[var(--ft-gold)]" />
              </div>
              <div className={`font-bold mt-1 tracking-tight text-[var(--ft-black)] ${k.small ? "text-sm" : "text-2xl"}`}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PIPELINE STRIP */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-testid="admin-inquiries-pipeline">
        <button
          onClick={() => setStatusFilter("")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            statusFilter === "" ? "bg-[var(--ft-black)] text-white" : "bg-[var(--ft-light-gray)] text-[var(--ft-charcoal)] hover:bg-[var(--ft-gold-soft)]"
          }`}
        >
          All ({inquiries.length})
        </button>
        {PIPELINE.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value === statusFilter ? "" : s.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              statusFilter === s.value ? "text-white" : "bg-[var(--ft-light-gray)] text-[var(--ft-charcoal)] hover:bg-[var(--ft-gold-soft)]"
            }`}
            style={statusFilter === s.value ? { background: STAGE_COLORS[s.value] } : {}}
            data-testid={`pipeline-filter-${s.value}`}
          >
            {s.label} ({stageCounts[s.value] || 0})
          </button>
        ))}
      </div>

      {/* SEARCH */}
      <div className="ft-card !p-3 flex items-center gap-3 mb-4">
        <Search size={18} className="text-[var(--ft-muted)] ml-1" />
        <input
          className="flex-1 outline-none bg-transparent text-sm"
          placeholder="Search name, email, company, brand, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="admin-inquiries-search"
        />
        {search && <button onClick={() => setSearch("")} className="text-xs text-[var(--ft-muted)] hover:text-[var(--ft-black)]">Clear</button>}
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : inquiries.length === 0 ? (
        <div className="ft-card text-center py-16">
          <MessageSquare size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">No inquiries match your filter.</h3>
        </div>
      ) : (
        <div className="ft-card !p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-inquiries-table">
            <thead className="bg-[var(--ft-light-gray)] text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-[var(--ft-charcoal)]">
              <tr>
                <th className="text-left p-4">Prospect</th>
                <th className="text-left p-4">Brand</th>
                <th className="text-left p-4">Package</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Last Contact</th>
                <th className="text-left p-4">Follow-Up</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((i) => (
                <tr key={i.id} className="border-t border-[var(--ft-border)] hover:bg-[var(--ft-light-gray)] cursor-pointer" onClick={() => setSelected(i)} data-testid={`inquiry-row-${i.id}`}>
                  <td className="p-4">
                    <div className="font-semibold text-[var(--ft-black)]">{i.full_name}</div>
                    <div className="text-xs text-[var(--ft-muted)] mt-0.5">{i.company || i.email}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm">{i.brand_name}</div>
                    {i.design_edition_label && <div className="text-[0.65rem] text-[var(--ft-muted)] mt-0.5">{i.design_edition_label.split("·")[0].trim()}</div>}
                  </td>
                  <td className="p-4 text-xs text-[var(--ft-charcoal)]">{(i.package_label || "").split("—")[0].trim()}</td>
                  <td className="p-4">
                    <select
                      value={i.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); updateInquiry(i.id, { status: e.target.value }); }}
                      className="text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer"
                      style={{ background: STAGE_COLORS[i.status] || "#666", color: "#fff" }}
                      data-testid={`inquiry-status-${i.id}`}
                    >
                      {PIPELINE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="p-4 text-xs text-[var(--ft-muted)]">{i.last_contacted_at ? new Date(i.last_contacted_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4 text-xs text-[var(--ft-muted)]">{i.follow_up_at ? new Date(i.follow_up_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <a href={`mailto:${i.email}?subject=Your FLOWTALIS Turnkey Brand inquiry — ${i.brand_name}`}
                         title="One-click email"
                         className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center"
                         data-testid={`inquiry-email-${i.id}`}>
                        <Mail size={14} />
                      </a>
                      <button onClick={() => copyEmail(i.email)} title="Copy email"
                              className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center">
                        <Copy size={14} />
                      </button>
                    </div>
                  </td>                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <InquiryDrawer inquiry={selected} onClose={() => setSelected(null)} onUpdate={updateInquiry} />}
    </AdminLayout>
  );
};

const InquiryDrawer = ({ inquiry, onClose, onUpdate }) => {
  const [note, setNote] = useState("");
  const [assigned, setAssigned] = useState(inquiry.assigned_to || "");
  const [followUp, setFollowUp] = useState(inquiry.follow_up_at || "");
  const [saving, setSaving] = useState(false);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    await onUpdate(inquiry.id, { note });
    setNote("");
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-inquiry-drawer">
        <div className="p-6 border-b border-[var(--ft-border)] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="ft-eyebrow">Inquiry Detail</div>
            <h2 className="text-xl font-bold mt-1">{inquiry.full_name}</h2>
            <p className="text-xs text-[var(--ft-muted)] mt-0.5">Submitted {new Date(inquiry.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="h-10 w-10 rounded-lg hover:bg-[var(--ft-light-gray)] flex items-center justify-center">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Prospect */}
          <Section title="Prospect">
            <KV k="Full Name" v={inquiry.full_name} />
            <KV k="Company" v={inquiry.company || "—"} />
            <KV k="Email" v={<a href={`mailto:${inquiry.email}`} className="text-[var(--ft-gold)] hover:underline">{inquiry.email}</a>} />
            <KV k="Phone" v={<a href={`tel:${inquiry.phone}`} className="text-[var(--ft-gold)] hover:underline">{inquiry.phone}</a>} />
          </Section>

          {/* Brand */}
          <Section title="Interest">
            <KV k="Turnkey Brand" v={inquiry.brand_name} />
            {inquiry.design_edition_label && <KV k="Design Edition" v={inquiry.design_edition_label} />}
            <KV k="Package" v={inquiry.package_label} />
            <KV k="Purchase Option" v={inquiry.purchase_option_label} />
            <KV k="Preferred Beverage" v={inquiry.preferred_beverage_label} />
            <KV k="Timeframe" v={inquiry.timeframe_label} />
            {inquiry.additional_info && <KV k="Notes from Prospect" v={<span className="whitespace-pre-wrap">{inquiry.additional_info}</span>} />}
          </Section>

          {/* Ownership */}
          <Section title="Ownership & Follow-Up">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="ft-label"><User size={12} className="inline mr-1" /> Assigned To</label>
                <input className="ft-input" value={assigned} onChange={(e) => setAssigned(e.target.value)} placeholder="e.g. Toia W." data-testid="inquiry-assigned" />
              </div>
              <div>
                <label className="ft-label"><Calendar size={12} className="inline mr-1" /> Follow-Up Date</label>
                <input type="date" className="ft-input" value={followUp?.slice(0, 10) || ""} onChange={(e) => setFollowUp(e.target.value)} data-testid="inquiry-followup" />
              </div>
            </div>
            <button
              onClick={() => onUpdate(inquiry.id, { assigned_to: assigned || null, follow_up_at: followUp || null })}
              className="ft-btn-secondary mt-3"
              data-testid="inquiry-save-ownership"
            >
              Save Ownership
            </button>
          </Section>

          {/* Add Note */}
          <Section title="Add Internal Note">
            <textarea
              className="ft-input min-h-[80px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Left voicemail, sent follow-up email, prospect requested pricing…"
              data-testid="inquiry-note-input"
            />
            <button onClick={addNote} disabled={saving || !note.trim()} className="ft-btn-primary mt-3" data-testid="inquiry-note-submit">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Add Note
            </button>
          </Section>

          {/* Activity Timeline */}
          <Section title="Activity Timeline">
            {(inquiry.activity || []).length === 0 ? (
              <p className="text-sm text-[var(--ft-muted)]">No activity recorded yet.</p>
            ) : (
              <ol className="space-y-3" data-testid="inquiry-activity">
                {[...(inquiry.activity || [])].reverse().map((a, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="mt-1.5 h-2 w-2 rounded-full bg-[var(--ft-gold)] shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-[var(--ft-muted)] flex items-center gap-2">
                        <Clock size={11} /> {new Date(a.at).toLocaleString()} · <span className="font-semibold text-[var(--ft-charcoal)]">{a.actor}</span>
                      </div>
                      <div className="text-sm text-[var(--ft-charcoal)] mt-0.5 whitespace-pre-wrap">{a.detail}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          {/* Production Tracker — 12-stage stage advancement */}
          <ProductionTrackerAdmin inquiry={inquiry} onUpdate={onUpdate} />
        </div>
      </div>
    </div>
  );
};

const PRODUCTION_STAGES = [
  "design", "artwork_submitted", "proofing", "client_approval",
  "plate_production", "printing", "filling", "quality_control",
  "production_complete", "shipping_scheduled", "shipped", "delivered",
];
const PRODUCTION_LABELS = {
  design: "Design", artwork_submitted: "Artwork Submitted", proofing: "Proofing",
  client_approval: "Client Approval", plate_production: "Plate Production",
  printing: "Printing", filling: "Filling", quality_control: "Quality Control",
  production_complete: "Production Complete", shipping_scheduled: "Shipping Scheduled",
  shipped: "Shipped", delivered: "Delivered",
};

const ProductionTrackerAdmin = ({ inquiry, onUpdate }) => {
  const [stage, setStage] = useState(inquiry.production_stage || "");
  const [eta, setEta] = useState((inquiry.estimated_completion || "").slice(0, 10));
  const [clientUpdate, setClientUpdate] = useState("");
  const [internal, setInternal] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!stage) return toast.error("Select a production stage.");
    setSaving(true);
    try {
      await api.patch(`/admin/production/inquiry/${inquiry.id}`, {
        order_type: "inquiry",
        production_stage: stage,
        estimated_completion: eta || null,
        client_update: clientUpdate || null,
        internal_note: internal || null,
      });
      toast.success(`Production → ${PRODUCTION_LABELS[stage]}`);
      setClientUpdate("");
      setInternal("");
      onUpdate(inquiry.id, {});  // triggers refresh
    } catch (err) { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  const idx = PRODUCTION_STAGES.indexOf(inquiry.production_stage);

  return (
    <Section title="Production Tracker">
      {idx >= 0 && (
        <>
          <div className="text-xs text-[var(--ft-muted)] mb-2">Current: <span className="font-semibold text-[var(--ft-charcoal)]">{PRODUCTION_LABELS[inquiry.production_stage]}</span> · Stage {idx + 1} of {PRODUCTION_STAGES.length}</div>
          <div className="flex gap-1 mb-4">
            {PRODUCTION_STAGES.map((s, i) => (
              <div key={s} className="flex-1 h-1.5 rounded-full" style={{ background: i <= idx ? "var(--ft-gold)" : "var(--ft-border)" }} title={PRODUCTION_LABELS[s]} />
            ))}
          </div>
        </>
      )}
      <div className="grid gap-2">
        <select className="ft-input" value={stage} onChange={(e) => setStage(e.target.value)} data-testid="admin-production-stage">
          <option value="">— Advance production stage —</option>
          {PRODUCTION_STAGES.map((s) => <option key={s} value={s}>{PRODUCTION_LABELS[s]}</option>)}
        </select>
        <input type="date" className="ft-input" value={eta} onChange={(e) => setEta(e.target.value)} placeholder="Estimated completion" data-testid="admin-production-eta" />
        <textarea className="ft-input min-h-[60px]" value={clientUpdate} onChange={(e) => setClientUpdate(e.target.value)} placeholder="Client-facing update (visible in timeline + email)" data-testid="admin-production-client-note" />
        <textarea className="ft-input min-h-[50px]" value={internal} onChange={(e) => setInternal(e.target.value)} placeholder="Internal note (admin-only)" data-testid="admin-production-internal-note" />
        <button onClick={save} disabled={saving || !stage} className="ft-btn-primary" data-testid="admin-production-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Update Production Stage
        </button>
      </div>
    </Section>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-[var(--ft-gold)] mb-3">{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-sm py-1.5 border-b border-[var(--ft-border)] last:border-0">
    <span className="text-[var(--ft-muted)] uppercase text-[0.7rem] tracking-[0.16em] font-semibold shrink-0">{k}</span>
    <span className="text-right text-[var(--ft-charcoal)] max-w-[65%] break-words">{v}</span>
  </div>
);

export default AdminTurnkeyInquiries;
